// "Friends playing now" (1.6, docs/MULTIPLAYER.md): the worlds that are online, for a signed-in player.
// Browser globals are only read when used, so the Node tests can load it.
import { identity } from './identity.js';

export const LOBBY_URL = '/api/room/online';
const MAX_WORLDS = 40;
const text = (v, max) => typeof v === 'string' && v.length > 0 && v.length <= max;

// Only well-formed worlds reach the screen (their names and codes are escaped there too).
function cleanWorlds(list) {
  if (!Array.isArray(list)) return null;
  return list
    .filter((w) => w && text(w.code, 20) && text(w.ownerName, 40) && Number.isInteger(w.online) && w.online >= 0)
    .slice(0, MAX_WORLDS)
    .map((w) => ({ code: w.code, ownerName: w.ownerName, online: w.online, locked: !!w.locked }));
}

// One look at the list: {ok:true, worlds, you} or {ok:false, error:'signin'|'offline'|'server'}.
export async function fetchOnlineWorlds({ fetchImpl = globalThis.fetch, token = identity.data?.token, timeout = 5000 } = {}) {
  if (!token) return { ok: false, error: 'signin' };
  if (typeof fetchImpl !== 'function') return { ok: false, error: 'offline' };
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  const late = new Promise((resolve) => {
    timer = setTimeout(() => {
      ctrl?.abort();
      resolve(null);
    }, timeout);
  });
  const ask = async () => {
    const res = await fetchImpl(LOBBY_URL, { headers: { authorization: `Bearer ${token}` }, cache: 'no-store', signal: ctrl?.signal });
    if (res.status === 401) return { ok: false, error: 'signin' };
    if (!res.ok) return { ok: false, error: 'server' };
    let json = null;
    try {
      json = await res.json();
    } catch (e) {
      /* not JSON */
    }
    const worlds = json?.ok ? cleanWorlds(json.worlds) : null;
    return worlds ? { ok: true, worlds, you: typeof json.you === 'string' ? json.you : null } : { ok: false, error: 'server' };
  };
  try {
    return (await Promise.race([ask(), late])) || { ok: false, error: 'offline' };
  } catch (e) {
    return { ok: false, error: 'offline' };
  } finally {
    clearTimeout(timer);
  }
}

// Asks at once, then every `every` ms while a screen or panel shows the list. A hidden page asks nothing, a player
// without a valid sign-in gets 'signin' without a request, and once `active()` is false the poller stops itself.
export class LobbyPoller {
  constructor({ every = 10000, onUpdate = () => {}, fetchImpl, active = () => true, timers, identity: who = identity, page = globalThis.document } = {}) {
    this.every = every;
    this.onUpdate = onUpdate;
    this.fetchImpl = fetchImpl;
    this.active = active;
    this.timers = timers || { setInterval: (f, ms) => globalThis.setInterval(f, ms), clearInterval: (id) => globalThis.clearInterval(id) };
    this.identity = who;
    this.page = page;
    this.timer = null;
    this.busy = null; // the request in flight
  }

  get running() {
    return this.timer !== null;
  }

  start() {
    if (this.running) return this.busy || Promise.resolve(null);
    this.timer = this.timers.setInterval(() => {
      if (!this.page?.hidden) this.poll();
    }, this.every);
    return this.poll();
  }

  stop() {
    if (this.timer !== null) this.timers.clearInterval(this.timer);
    this.timer = null;
  }

  // Ask now, sharing a request already in flight. Resolves with what onUpdate was given, or null.
  poll() {
    if (!this.running) return Promise.resolve(null);
    if (!this.active()) {
      this.stop();
      return Promise.resolve(null);
    }
    if (this.busy) return this.busy;
    if (!this.identity.tokenValid()) return Promise.resolve(this.update({ ok: false, error: 'signin' }));
    const busy = fetchOnlineWorlds({ fetchImpl: this.fetchImpl, token: this.identity.data?.token }).then((r) => {
      if (this.busy === busy) this.busy = null;
      return this.running && this.active() ? this.update(r) : (this.stop(), null);
    });
    return (this.busy = busy);
  }

  // A fresh answer even while a request is in flight (it may have started before something changed).
  async refresh() {
    await this.busy;
    return this.poll();
  }

  update(r) {
    try {
      this.onUpdate(r);
    } catch (e) {
      globalThis.console?.error(e);
    }
    return r;
  }
}
